import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight, Add, Weight, Watch } from "@material-symbols-svg/react";

import BottomSheet from "../../BottomSheet";
import LoaderFullScreen from "src/components/common/Loader/LoaderFullScreen";
import TimerDisplay from "src/components/app/TimerDisplay";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { store } from "src/redux/store";
import * as cgmActions from "src/redux/slices/cgmSlice";
import { Device } from "src/types/device.types";
import { DEVICE_IMAGES } from "src/constants/device";
import { cgmManager } from "src/services/cgm/cgmManager";
import useCgmHook from "src/hooks/useCgm.hook";
import { CGM_READER_DEVICE_ID } from "src/constants/cgm";
import { CgmStatusData } from "src/types/cgm.types";
import { STORAGE_KEYS } from "src/constants/common";
import storage from "src/helpers/storage";

interface ConnectedBottomSheetProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onAddDeviceClick: () => void;
  startConnectionFlow: (deviceId: string) => void;
}

const ConnectedBottomSheet: React.FC<ConnectedBottomSheetProps> = ({
  show = false,
  setShow,
  onAddDeviceClick,
  startConnectionFlow,
}) => {
  const dispatch = useAppDispatch();
  const { connectedDevices } = useAppSelector((state) => state.device);
  const { statusAndSensorLife } = useAppSelector((state) => state.cgm);
  const [activeCgm, setActiveCgm] = useState<CgmStatusData | null>(null);
  const [cgmSyncStarted, setCgmSyncStarted] = useState(false);

  const connectedDevicesCount = connectedDevices?.data?.length || 0;

  /** Stable while device set is unchanged — avoids re-dispatch when `data` array ref churns. */
  const connectedDeviceIdsKey = useMemo(
    () =>
      (connectedDevices?.data ?? [])
        .map((d) => d.id)
        .sort()
        .join("\0"),
    [connectedDevices?.data]
  );

  useEffect(() => {
    if (!connectedDevices?.isLoading && connectedDevices.data?.length === 0) {
      onAddDeviceClick();
    }
  }, [connectedDevices, onAddDeviceClick]);

  useEffect(() => {
    if (!show) return;
    const hasCgmReader = connectedDevices?.data?.some(
      (d) => d.id === CGM_READER_DEVICE_ID
    );
    if (!hasCgmReader) return;
    const stored = storage.getItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    if (stored) {
      const endMs = new Date(stored).getTime();
      if (Number.isFinite(endMs) && endMs > Date.now()) {
        dispatch(cgmActions.warmupStarted(stored));
      } else {
        storage.removeItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
      }
    }
    dispatch(cgmActions.getCgmStatusRequest());
  }, [show, connectedDeviceIdsKey, dispatch]);

  useEffect(() => {
    if (statusAndSensorLife?.data) {
      setActiveCgm(statusAndSensorLife?.data?.[0] ?? null);
    }
  }, [statusAndSensorLife]);

  useEffect(() => {
    if (activeCgm) {
      const isStopped =
        activeCgm.status === "EXPIRED" ||
        activeCgm.endDate < new Date().toISOString();
      if (!isStopped) {
        if (!cgmSyncStarted) {
          setCgmSyncStarted(true);
          cgmManager.refetchCgmStatus(undefined, store.getState().cgm);
        }
      }
    }
  }, [activeCgm]);

  if (connectedDevices.isLoading) {
    return <LoaderFullScreen show={connectedDevices.isLoading} />;
  }

  return (
    <BottomSheet
      show={show}
      handleClose={() => setShow(false)}
      height={50}
      header={
        <div className="flex items-center justify-between p-4 w-full">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--icon-bg-primary)]">
              <Watch className="w-6 h-6 text-[var(--app-primary)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">My Devices</h2>
              <p className="text-sm text-[var(--text-secondary)]">
                {connectedDevicesCount} connected
              </p>
            </div>
          </div>
          <button
            className="bg-[var(--app-primary)] rounded-lg p-2"
            onClick={onAddDeviceClick}
            aria-label="Add device"
          >
            <Add className="w-4 h-4 text-white" />
          </button>
        </div>
      }
    >
      <div className="p-4 flex flex-col gap-4">
        {connectedDevicesCount === 0 ? (
          <div className="flex items-center justify-center h-full pb-5">
            <p className="text-sm text-[var(--text-secondary)]">
              No devices connected
            </p>
          </div>
        ) : (
          connectedDevices?.data?.map((device: Device) => {
            return (
              <DeviceCard
                key={device.id}
                device={device}
                startConnectionFlow={startConnectionFlow}
              />
            );
          })
        )}
      </div>
    </BottomSheet>
  );
};

export default ConnectedBottomSheet;

interface DeviceProps {
  device: Device;
  startConnectionFlow: (deviceId: string) => void;
}
export const DeviceCard: React.FC<DeviceProps> = ({
  device,
  startConnectionFlow,
}) => {
  const {
    glucoseValue,
    timestamp,
    status: cgmUiStatus,
    warmupCountdownMMSS,
  } = useCgmHook();

  const deviceImage = DEVICE_IMAGES[device.id as keyof typeof DEVICE_IMAGES]
    ? DEVICE_IMAGES[device.id as keyof typeof DEVICE_IMAGES].image
    : null;

  return (
    <div
      key={device.id}
      className="relative bg-[var(--surface-secondary)] rounded-xl overflow-hidden border border-gray-100"
    >
      <div className="flex items-center gap-3 p-4">
        <div className="w-12 h-12 rounded-xl bg-[var(--icon-bg-primary)] flex items-center justify-center flex-shrink-0">
          {deviceImage ? (
            <img
              src={deviceImage}
              alt={device.name}
              className="w-12 h-12 object-contain"
            />
          ) : (
            <Weight className="w-6 h-6 text-[var(--app-primary)]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate">{device.name}</h3>
          <p className="text-sm text-[var(--text-secondary)]">
            {device.description}
          </p>
        </div>
        <button
          onClick={() => startConnectionFlow(device.id)}
          className="flex-shrink-0 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Sync scale"
        >
          <ChevronRight className="w-5 h-5 text-[var(--text-secondary)]" />
        </button>
      </div>
      <div className="text-xs flex items-center justify-between gap-3 p-4 bg-[var(--app-primary-background)]">
        <div className="text-[var(--text-tertiary)] flex items-center gap-0.5">
          <div className="text-gray-500 font-medium">Last Sync:</div>
          <div className="text-green-600 font-semibold">
            <TimerDisplay
              timestamp={
                timestamp ?? device?.last_sync_timestamp ?? ("" as string)
              }
              showLiveUpdate={true}
            />
          </div>
        </div>
        <div className="text-[var(--text-tertiary)] flex items-center gap-0.5">
          <div className="text-gray-500 font-medium">Last Reading:</div>
          <div className="text-green-600 font-semibold">
            {device?.id === "goodflip-cgm-reader" && glucoseValue != null
              ? `${glucoseValue} mg/dL`
              : device?.last_reading
              ? `${device?.last_reading} ${device?.unit}`
              : "-- mg/dL"}
          </div>
        </div>
      </div>

      {device?.id === CGM_READER_DEVICE_ID && (
        <StatusBadge type={cgmUiStatus.type}>
          {cgmUiStatus.code === "WARMUP"
            ? warmupCountdownMMSS
              ? `Warmup · ${warmupCountdownMMSS}`
              : cgmUiStatus.label
            : cgmUiStatus.label}
        </StatusBadge>
      )}
    </div>
  );
};

interface StatusBadgeProps {
  type: "success" | "warning" | "danger" | "info";
  children: React.ReactNode;
}
const StatusBadge: React.FC<StatusBadgeProps> = ({ type, children }) => {
  const colorMap = {
    success: "bg-green-700",
    warning: "bg-yellow-500",
    danger: "bg-red-500",
    info: "bg-blue-500",
    neutral: "bg-gray-500",
  };
  return (
    <div
      className={`absolute top-0 right-0 ${colorMap[type]} text-white text-xs px-2 py-1 rounded-bl-xl`}
    >
      <p className="text-white text-xs">{children}</p>
    </div>
  );
};
